import { t } from 'i18next';

import { capitalize } from '@/utils/capitalize';
import i18n from '../i18n';
import { Company } from './Company';
import { CourseSection } from './CourseSection';
import { DeactivationReason } from './DeactivationReason';
import { Item } from './Item';
import { Person } from './Person';
import { Term } from './Term';

export interface ExtraQuizTime {
  assignmentId: number;
  extraQuizTime: number;
}

export enum EnrollmentType {
  STUDENT = 'STUDENT',
  TA = 'TA',
  INSTRUCTOR = 'INSTRUCTOR',
  OBSERVER = 'OBSERVER',
}

export enum EnrollmentRole {
  COURSE_PARTICIPANT = 'COURSE_PARTICIPANT',
  COURSE_EDITOR = 'COURSE_EDITOR',
  COURSE_ADMINISTRATOR = 'COURSE_ADMINISTRATOR',
}

export const AllAssignments = 0;

export interface ExtendedDueDate {
  assignmentId: number;
  extendedDueDate: Date;
}

export interface EnrollmentExtensions {
  extendedDueDates?: any;
  extraQuizTime?: any;
  extraTestTime?: any;
  extraQuizAttempts?: any;
}

export const displayEnrollmentRole = (role: EnrollmentRole): string => {
  return role
    .split('_')
    .map((part: string) => capitalize(part.toLowerCase()))
    .join(' ');
};

export const displayDeactivationReason = (reason: DeactivationReason): string => {
  if (reason) {
    switch (reason) {
      case DeactivationReason.STUDENT_OPT_OUT:
        return i18n.t('optOut');
      case DeactivationReason.UNENROLLMENT:
        return i18n.t('unenrolled');
      default:
        return capitalize(reason.toLowerCase());
    }
  } else {
    return '-';
  }
};

export const displayEnrollmentType = (type: EnrollmentType): string => {
  if (!type) {
    return '-';
  }

  if (type === EnrollmentType.TA) {
    return 'Teaching Assistant';
  }

  return capitalize(type.toLowerCase());
};

export interface Enrollment extends Item {
  schoolId: number;
  school?: Company;
  termId: number;
  term?: Term;
  courseSectionId?: number;
  courseSection?: CourseSection;
  personId?: number;
  person?: Person;
  type: EnrollmentType;
  role: EnrollmentRole;
  lmsId?: string;
  sisId?: string;
  ltiId?: string;
  posId?: string;
  lmsCourseId?: string;
  lmsSectionId?: string;
  lmsUserId?: string;
  sisCourseId?: string;
  sisSectionId?: string;
  sisUserId?: string;
  deactivationReason?: DeactivationReason;

  copiedBytes: number;
  printedBytes: number;
  downloadedBytes: number;

  extensions: EnrollmentExtensions;

  checkoutSessionId?: string;
  checkoutSessionExpiresAt?: Date;

  checkoutPaymentId?: string;
  checkoutPaymentAt?: Date;

  checkoutRefundId?: string;
  checkoutRefundAt?: Date;

  accessCodeId?: string;
  isPreview?: boolean;
  authToken?: string;
}

export const isAccessCodePaid = (enrollment: Enrollment | null): boolean => {
  return enrollment?.accessCodeId !== undefined && enrollment?.accessCodeId !== null;
};
export const isRetailPaid = (enrollment: Enrollment | null): boolean => {
  return enrollment?.checkoutPaymentId !== undefined && enrollment?.checkoutPaymentId !== null;
};

export const isRetailRefunded = (enrollment: Enrollment | null): boolean => {
  return enrollment?.checkoutRefundId !== undefined && enrollment?.checkoutRefundId !== null;
};

export interface EnrollmentFilterList {
  personId?: number;
  courseSectionId?: number;
  termId?: number;
  search?: string;
}

export const purchaseLabel = (enrollment: Enrollment | null) => {
  if (!enrollment) {
    return '';
  }

  return isAccessCodePaid(enrollment)
    ? t('accessCodePurchased')
    : isRetailRefunded(enrollment)
      ? t('refunded')
      : isRetailPaid(enrollment)
        ? t('retailPurchased')
        : t('accessProgram');
};
